import React, { useEffect, useState } from 'react';
import {
  ServerIcon,
  CodeBracketIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import AuthenticatedLayout from '../components/AuthenticatedLayout';
import SftpCredentialsSection from '../components/uploads/SftpCredentialsSection';
import PasswordResetModal from '../components/uploads/PasswordResetModal';
import WebUploadSection from '../components/uploads/WebUploadSection';
import uploadsService from '../services/uploads.service';
import { SFTPCredentials } from '../types/uploads.types';

const Uploads: React.FC = () => {
  const { t } = useTranslation('uploads');
  const [credentials, setCredentials] = useState<SFTPCredentials | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [newPassword, setNewPassword] = useState<string | undefined>();

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      setIsLoading(true);
      const data = await uploadsService.getSFTPCredentials();
      setCredentials(data);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 202) {
        setError('provisioning');
      } else if (err.response?.status === 503) {
        setError('failed');
      } else {
        setError('unknown');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (password: string) => {
    try {
      setIsResetting(true);
      setResetError(null);
      const result = await uploadsService.resetSFTPPassword({ current_password: password });
      setNewPassword(result.new_password);
      await fetchCredentials();
    } catch (err: any) {
      if (err.response?.status === 403) {
        setResetError(t('resetModal.incorrectPassword'));
      } else {
        setResetError(t('resetModal.error'));
      }
    } finally {
      setIsResetting(false);
    }
  };

  const handleCloseModal = () => {
    setShowResetModal(false);
    setResetError(null);
    setNewPassword(undefined);
  };

  const connectionGuide = [
    {
      step: 1,
      title: t('guide.step1.title'),
      description: t('guide.step1.description'),
    },
    {
      step: 2,
      title: t('guide.step2.title'),
      description: t('guide.step2.description'),
    },
    {
      step: 3,
      title: t('guide.step3.title'),
      description: t('guide.step3.description'),
    },
    {
      step: 4,
      title: t('guide.step4.title'),
      description: t('guide.step4.description'),
    },
  ];

  return (
    <AuthenticatedLayout>
      <header>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
            {t('page.title')}
          </h1>
          <p className="mt-2 text-sm text-gray-500">{t('page.description')}</p>
        </div>
      </header>

      <main>
        <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="px-4 py-8 sm:px-0">
            {/* Upload Methods Overview */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 mb-8">
              <div className="bg-white shadow rounded-lg p-6 border-l-4 border-indigo-500">
                <div className="flex items-center">
                  <ServerIcon className="h-8 w-8 text-indigo-500" />
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">{t('methods.sftp.name')}</h3>
                    <p className="text-sm text-gray-500">{t('methods.sftp.fullDescription')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6 border-l-4 border-green-500">
                <div className="flex items-center">
                  <CodeBracketIcon className="h-8 w-8 text-green-500" />
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">{t('methods.api.name')}</h3>
                    <p className="text-sm text-gray-500">{t('methods.api.fullDescription')}</p>
                    <a
                      href="/api-keys"
                      className="inline-flex items-center mt-2 text-sm text-indigo-600 hover:text-indigo-500"
                    >
                      {t('methods.api.viewApiKeys')}
                      <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6 border-l-4 border-purple-500">
                <div className="flex items-center">
                  <CloudArrowUpIcon className="h-8 w-8 text-purple-500" />
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('methods.web.name')}
                    </h3>
                    <p className="text-sm text-gray-500">{t('methods.web.fullDescription')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Web Upload Section */}
            <div className="mb-8">
              <WebUploadSection />
            </div>

            {/* SFTP Credentials Section */}
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : error ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      {error === 'provisioning'
                        ? t('page.provisioning')
                        : error === 'failed'
                        ? t('page.provisioningFailed')
                        : t('page.error')}
                    </h3>
                    <p className="mt-2 text-sm text-yellow-700">
                      {error === 'provisioning'
                        ? t('page.provisioningDescription')
                        : t('page.errorDescription')}
                    </p>
                    {error === 'provisioning' && (
                      <button
                        onClick={fetchCredentials}
                        className="mt-3 text-sm font-medium text-yellow-800 hover:text-yellow-700"
                      >
                        {t('page.refresh')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : credentials && (
              <SftpCredentialsSection
                credentials={credentials}
                onResetPassword={() => setShowResetModal(true)}
              />
            )}

            {/* Connection Guide */}
            <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-6 w-6 text-indigo-500 mr-3" />
                  <h3 className="text-lg font-medium text-gray-900">{t('guide.title')}</h3>
                </div>
                <p className="mt-1 text-sm text-gray-500">{t('guide.description')}</p>
              </div>
              <div className="px-4 py-5 sm:p-6">
                <ol className="space-y-6">
                  {connectionGuide.map((item) => (
                    <li key={item.step} className="flex">
                      <div className="flex-shrink-0">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 text-sm font-medium">
                          {item.step}
                        </span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                        <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                {/* Example Command */}
                {credentials && (
                  <div className="mt-8 bg-gray-900 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-2">{t('guide.exampleCommand')}</p>
                    <code className="text-sm text-green-400 font-mono">
                      sftp -P {credentials.port} {credentials.username}@{credentials.host}
                    </code>
                  </div>
                )}

                {/* Directory Structure */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">{t('guide.directoryStructure')}</h4>
                  <pre className="text-sm text-gray-600 font-mono">
{`/inbox        ${t('guide.directories.inbox')}
/processed    ${t('guide.directories.processed')}
/failed       ${t('guide.directories.failed')}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <PasswordResetModal
        isOpen={showResetModal}
        onClose={handleCloseModal}
        onConfirm={handleResetPassword}
        newPassword={newPassword}
        isLoading={isResetting}
        error={resetError}
      />
    </AuthenticatedLayout>
  );
};

export default Uploads;
